import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, User, FileText, Bed, Headphones, Music, Beer, Gift, Camera, LogOut, Menu, X, MessageCircle, PartyPopper, Users } from 'lucide-react';

const LOGO_URL = '/logo.png';

const navItems = [
  { path: '/portal', icon: LayoutDashboard, label: 'Overblik', end: true },
  { path: '/portal/profile', icon: User, label: 'Min profil' },
  { path: '/portal/directory', icon: Users, label: 'Mød gæsterne' },
  { path: '/portal/briefing', icon: FileText, label: 'Eventbriefing' },
  { path: '/portal/accommodation', icon: Bed, label: 'Overnatning' },
  { path: '/portal/support', icon: Headphones, label: 'Spørgsmål og svar' },
  { path: '/portal/music', icon: Music, label: 'Musikønsker' },
  { path: '/portal/chat', icon: MessageCircle, label: 'Gæstechat' },
  { path: '/portal/entertainment', icon: PartyPopper, label: 'Indslag & underholdning' },
  { path: '/portal/bar', icon: Beer, label: 'Barbestilling' },
  { path: '/portal/wishes', icon: Gift, label: 'Ønskeliste' },
  { path: '/portal/photos', icon: Camera, label: 'Fotoprogram' },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#020617] flex overflow-x-hidden">
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-slate-900/95 backdrop-blur-xl border-r border-slate-800 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:inset-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="portal-sidebar">
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
          <img src={LOGO_URL} alt="Molin30" className="h-8" />
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white" aria-label="Luk menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 border-b border-slate-800/50">
          <p className="text-sm font-medium text-slate-200 truncate">{user?.full_name || 'Gæst'}</p>
          <p className="text-xs text-slate-500 truncate font-mono">{user?.display_email || user?.private_email || user?.corporate_email || ''}</p>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {navItems.map(({ path, icon: Icon, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}-link`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 w-full transition-colors"
            data-testid="portal-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Log ud
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 flex items-center px-4 lg:px-6">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="sidebar-toggle" aria-label="Åbn menu">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white">
              {(user?.full_name || 'G').charAt(0)}
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-x-hidden p-4 lg:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
